/**
 * Add a client.
 *
 * Three interesting parts.
 *
 * **The failure handling.** Three refusals are *expected* here and each is a
 * different sentence:
 *
 * - `VALIDATION_ERROR` — the GSTIN is malformed, its checksum fails, or the
 *   PAN contradicts the one inside the GSTIN. The user's typo, three ways.
 * - `CONFLICT` — this firm already has that GSTIN. Their own record, safe to name.
 * - `USAGE_LIMIT_EXCEEDED` — the plan's client cap. **Not** an error; a
 *   commercial fact, and the screen must say so with a way forward
 *   (APP_DEVELOPMENT_GUIDE §7.3: render `SEAT_LIMIT_EXCEEDED` as "5 of 5 seats
 *   in use", not as "something went wrong").
 *
 * **The GSTIN check (FR-C13).** State code drives place of supply, the legal
 * name is printed on the workbook a client approves — typed by hand, each is a
 * transcription error waiting to decide someone's tax. So the check reads what
 * the GSTIN itself encodes (state, PAN, entity type).
 *
 * **The GST portal, by two routes (ADR-0033, FR-C17).** The register's fields —
 * names, status, constitution, registration type, date, address — come from the
 * portal's public search, and the person chooses how: **here, with a CAPTCHA**
 * (ProGST's server asks the portal; the portal sees the server), or **on the
 * portal, then paste** (the person's own tab; the portal sees them). Either way
 * the CAPTCHA is the person's, and the answer only fills the form. **The paste
 * route is parked** (owner, 2026-10-03; `PASTE_ROUTE_OFFERED`): its code stays,
 * unoffered, and the portal's own search is linked for the person to look up
 * themselves (FR-C13d's fallback).
 *
 * **The sources are labelled separately and must stay that way**:
 * "Proprietorship, read from your GSTIN" and "Proprietorship, from the GST
 * portal" are different claims. Everything filled stays editable; the firm
 * decides who they act for.
 *
 * The dialog is the shell's `<Modal>` (constitution §7).
 */

import { Modal } from '@fiveox/shell'
import { Alert, Button, Input, Textarea } from '@fiveox/ui'
import { ClipboardPaste, ExternalLink, RefreshCw, ScanLine, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

import {
  ApiError,
  type PortalCaptcha,
  type PortalSearch,
  type TaxpayerLookup,
  clients,
} from '../lib/api'
import { STORE_URL } from '../lib/config'
import {
  type FormFill,
  PASTE_ROUTE_OFFERED,
  PASTE_STEPS,
  REGISTRATION_TYPES,
  fillFromRegister,
  registrationTypeNote,
  sourceWords,
} from '../lib/portalLookup'

/**
 * Structural check, mirroring the service's `GSTIN_RE` **exactly**.
 *
 * The fourteenth character is `[0-9A-Z]`, not a literal `Z`. It is `Z` on
 * almost every registration, which is exactly why pinning it here was wrong
 * and invisible: this dialog refused `30AADCE8469R1CB` — a real,
 * checksum-valid registration — by disabling its own submit button, while the
 * service would have accepted it.
 *
 * Shape only. The mod-36 checksum is the service's to enforce.
 */
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][0-9A-Z][0-9A-Z]$/

/**
 * The GST portal's own public taxpayer search — the page the paste route sends
 * the person to, and the manual fallback (FR-C13d) beside the route through
 * ProGST or in its place. Checked 2026-10-02: it answers 200.
 */
const GST_PORTAL_SEARCH = 'https://services.gst.gov.in/services/searchtp'

type PortalMode = 'captcha' | 'paste' | null

export function NewClientDialog({
  onClose,
  onCreated,
  lookupAvailable = false,
  portalAvailable = false,
}: {
  onClose: () => void
  onCreated: () => void
  lookupAvailable?: boolean
  /** ADR-0033's server route is on in this deployment (`meta.taxpayer_lookup.portal`).
   *  The paste route needs nothing, and is offered only while `PASTE_ROUTE_OFFERED`. */
  portalAvailable?: boolean
}) {
  const [form, setForm] = useState<FormFill>({
    legalName: '',
    tradeName: '',
    constitution: '',
    registrationType: 'regular',
    registeredOn: '',
    addressLine: '',
    city: '',
    pincode: '',
  })
  const set = (key: keyof FormFill) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))
  const [gstin, setGstin] = useState('')
  const [error, setError] = useState<{ message: string; atCap: boolean } | null>(null)
  const [saving, setSaving] = useState(false)

  const [found, setFound] = useState<TaxpayerLookup | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkNote, setCheckNote] = useState<string | null>(null)

  // ADR-0033 — the portal, by the route the person chose.
  const [portalMode, setPortalMode] = useState<PortalMode>(null)
  const [captcha, setCaptcha] = useState<PortalCaptcha | null>(null)
  const [captchaAnswer, setCaptchaAnswer] = useState('')
  const [pasteText, setPasteText] = useState('')
  const [portalBusy, setPortalBusy] = useState(false)
  const [portalNote, setPortalNote] = useState<string | null>(null)
  const [portalFound, setPortalFound] = useState<PortalSearch | null>(null)

  const normalised = gstin.trim().toUpperCase()
  const gstinLooksRight = GSTIN_RE.test(normalised)

  /** A new GSTIN voids whatever was fetched or asked for the last one. */
  const changeGstin = (value: string) => {
    setGstin(value)
    setCaptcha(null)
    setCaptchaAnswer('')
    setPortalFound(null)
    setPortalNote(null)
  }

  const check = async () => {
    setChecking(true)
    setCheckNote(null)
    setFound(null)
    try {
      const result = (await clients.lookup(normalised)).data
      setFound(result)
      // Filled, not forced: a field the answer leaves blank stays as typed.
      if (result.derived.constitution) set('constitution')(result.derived.constitution)
      if (result.register) applyRegister(result.register)
    } catch (err) {
      setCheckNote(err instanceof ApiError ? err.message : 'The GSTIN could not be checked.')
    } finally {
      setChecking(false)
    }
  }

  const applyRegister = (register: NonNullable<PortalSearch['register']>) => {
    setForm((current) => fillFromRegister(current, register))
    setCheckNote(registrationTypeNote(register))
  }

  /** Through ProGST: a CAPTCHA for the person to read. */
  const askCaptcha = async () => {
    setPortalBusy(true)
    setPortalNote(null)
    setCaptchaAnswer('')
    try {
      setCaptcha((await clients.portalCaptcha(normalised)).data)
    } catch (err) {
      setCaptcha(null)
      setPortalNote(err instanceof ApiError ? err.message : 'The GST portal could not be asked.')
    } finally {
      setPortalBusy(false)
    }
  }

  const answered = (result: PortalSearch) => {
    if (result.result === 'wrong_captcha') {
      // The person tries again with a new picture; ProGST never does.
      setCaptcha(result.captcha ?? null)
      setCaptchaAnswer('')
      setPortalNote(result.message ?? 'The characters did not match. Try the new picture.')
      return
    }
    setPortalFound(result)
    setCaptcha(null)
    setPortalNote(null)
    if (result.register) applyRegister(result.register)
  }

  const fetchDetails = async () => {
    if (!captcha) return
    setPortalBusy(true)
    setPortalNote(null)
    try {
      answered((await clients.portalSearch(captcha.token, captchaAnswer.trim())).data)
    } catch (err) {
      setCaptcha(null)
      setPortalNote(err instanceof ApiError ? err.message : 'The GST portal search failed.')
    } finally {
      setPortalBusy(false)
    }
  }

  /** From the person's own tab: read the result page they pasted. */
  const readPaste = async () => {
    setPortalBusy(true)
    setPortalNote(null)
    try {
      answered((await clients.portalPasted(normalised, pasteText)).data)
    } catch (err) {
      setPortalNote(err instanceof ApiError ? err.message : 'The pasted page could not be read.')
    } finally {
      setPortalBusy(false)
    }
  }

  const choose = (mode: PortalMode) => {
    setPortalMode(mode)
    setPortalNote(null)
    if (mode === 'captcha' && !captcha) void askCaptcha()
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await clients.create({
        legal_name: form.legalName.trim(),
        gstin: normalised,
        trade_name: form.tradeName.trim() || undefined,
        constitution: form.constitution.trim() || undefined,
        registration_type: form.registrationType,
        address_line: form.addressLine.trim() || undefined,
        city: form.city.trim() || undefined,
        pincode: form.pincode.trim() || undefined,
        registered_on: form.registeredOn || undefined,
      })
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ message: err.message, atCap: err.code === 'USAGE_LIMIT_EXCEEDED' })
      } else {
        setError({ message: 'The client could not be added.', atCap: false })
      }
    } finally {
      setSaving(false)
    }
  }

  const derived = found?.derived
  const readFacts = derived
    ? [`State ${derived.state_code}`, `PAN ${derived.pan}`, derived.entity_type].filter(Boolean)
    : []
  const fetchedAt = portalFound?.fetched_at
    ? new Date(portalFound.fetched_at).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <Modal open onClose={onClose} title="Add a client">
      {/* Scrolls inside a height that always fits the window. The shell's Modal
          centres its panel in an overflow container (`sm:items-center`), so a panel
          taller than the window overflows UPWARD and its top — the GSTIN field —
          can never be scrolled to. Found in the browser look for E-04; the shell's
          to fix, recorded as a platform finding. */}
      <form onSubmit={submit} className="max-h-[calc(100vh-10rem)] space-y-4 overflow-y-auto pr-1">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="GSTIN"
              value={gstin}
              onChange={(e) => changeGstin(e.target.value)}
              required
              maxLength={15}
              placeholder="27AAAPA1234A1Z5"
              className="font-mono uppercase"
              hint={
                normalised.length === 15 && !gstinLooksRight
                  ? 'That does not look like a valid GSTIN.'
                  : undefined
              }
            />
          </div>
          {lookupAvailable && (
            <Button
              type="button"
              variant="outline"
              className="h-11 shrink-0"
              disabled={!gstinLooksRight}
              loading={checking}
              loadingText="Checking…"
              onClick={() => void check()}
            >
              <ScanLine size={16} /> Check GSTIN
            </Button>
          )}
        </div>

        {found && (
          <Alert tone="info">
            <p>
              <strong>Read from the GSTIN itself:</strong> {readFacts.join(' · ')}.
            </p>
            {derived?.is_additional_registration && (
              <p className="mt-1 text-sm">
                This PAN holds more than one registration in state {derived.state_code}.
              </p>
            )}
            {derived && !derived.is_ordinary_taxpayer && (
              <p className="mt-1 text-sm">
                The fourteenth character is not <code>Z</code> — this is not an ordinary
                taxpayer registration. Check the type before filing for it.
              </p>
            )}
            {found.also_filed_for_this_pan.length > 0 && (
              <p className="mt-1 text-sm">
                You already file for this taxpayer:{' '}
                {found.also_filed_for_this_pan
                  .map((c) => `${c.legal_name} (${c.gstin})`)
                  .join(', ')}
                .
              </p>
            )}
          </Alert>
        )}

        {found?.register && (
          <Alert tone={found.register.registration_status === 'Active' ? 'info' : 'warning'}>
            <p>
              <strong>The register reports</strong> this GSTIN as{' '}
              {found.register.registration_status ?? 'of unknown status'}
              {found.register.registered_on && `, registered ${found.register.registered_on}`}.
            </p>
          </Alert>
        )}

        {/* ADR-0033: the GST portal, by the route the person chooses. */}
        {gstinLooksRight && (
          <section aria-label="Get the details from the GST portal" className="space-y-3">
            <p className="text-sm font-medium text-foreground">
              Get the details from the GST portal
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {portalAvailable && !(portalMode === 'captcha' && captcha) && (
                <Button
                  type="button"
                  variant={portalMode === 'captcha' ? 'secondary' : 'outline'}
                  size="sm"
                  loading={portalBusy && !captcha}
                  loadingText="Asking the portal…"
                  onClick={() => choose('captcha')}
                >
                  <ShieldCheck size={15} />{' '}
                  {PASTE_ROUTE_OFFERED ? 'Here, with a CAPTCHA' : 'Fetch from the GST portal'}
                </Button>
              )}
              {PASTE_ROUTE_OFFERED && (
                <Button
                  type="button"
                  variant={portalMode === 'paste' ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => choose('paste')}
                >
                  <ClipboardPaste size={15} /> On the portal, then paste
                </Button>
              )}
            </div>

            {portalMode === 'captcha' && captcha && (
              <div className="space-y-2">
                <img
                  src={captcha.image}
                  alt="The GST portal's CAPTCHA — type the characters it shows"
                  className="h-12 rounded border border-border bg-background"
                />
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Input
                      label="Characters in the picture"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      maxLength={12}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    type="button"
                    className="h-11 shrink-0"
                    disabled={!captchaAnswer.trim()}
                    loading={portalBusy}
                    loadingText="Fetching…"
                    onClick={() => void fetchDetails()}
                  >
                    Fetch details
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 shrink-0"
                    disabled={portalBusy}
                    onClick={() => void askCaptcha()}
                    aria-label="Get a new picture"
                  >
                    <RefreshCw size={15} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  ProGST sends what you type to the GST portal with this GSTIN, once. The picture
                  expires in five minutes.
                </p>
              </div>
            )}

            {PASTE_ROUTE_OFFERED && portalMode === 'paste' && (
              <div className="space-y-2">
                <ol className="list-decimal space-y-0.5 pl-5 text-sm text-muted-foreground">
                  {PASTE_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                <a
                  href={GST_PORTAL_SEARCH}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary"
                >
                  Open the portal’s search <ExternalLink size={14} />
                </a>
                <Textarea
                  label="The portal's result page"
                  rows={5}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste the whole result page here"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!pasteText.trim()}
                  loading={portalBusy}
                  loadingText="Reading…"
                  onClick={() => void readPaste()}
                >
                  Read
                </Button>
              </div>
            )}

            {portalNote && <Alert tone="warning">{portalNote}</Alert>}

            {portalFound?.register && (
              <Alert
                tone={portalFound.register.registration_status === 'Active' ? 'info' : 'warning'}
              >
                <p>
                  <strong>{sourceWords(portalFound.source)}</strong>
                  {fetchedAt && ` at ${fetchedAt}`}: this GSTIN is{' '}
                  {portalFound.register.registration_status ?? 'of unknown status'}
                  {portalFound.register.registered_on &&
                    `, registered ${portalFound.register.registered_on}`}
                  . The details below are filled in — check them before adding the client.
                </p>
              </Alert>
            )}

            {/* FR-C13d's fallback, last: the person looks it up themselves and types it in. */}
            {!PASTE_ROUTE_OFFERED && (
              <p className="text-xs text-muted-foreground">
                {portalAvailable ? 'Or look it up ' : 'Look it up '}
                <a
                  href={GST_PORTAL_SEARCH}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-0.5 font-medium text-primary"
                >
                  on the GST portal yourself <ExternalLink size={12} />
                </a>{' '}
                and type the details below.
              </p>
            )}
          </section>
        )}

        {found?.register_gate && !portalFound && (
          <Alert tone="warning">
            <p>
              The registered name, status and address are not inside a GSTIN.{' '}
              {found.register_gate}
            </p>
          </Alert>
        )}

        {checkNote && <Alert tone="warning">{checkNote}</Alert>}

        <Input
          label="Legal name"
          value={form.legalName}
          onChange={(e) => set('legalName')(e.target.value)}
          required
          maxLength={200}
        />

        <Input
          label="Trade name"
          value={form.tradeName}
          onChange={(e) => set('tradeName')(e.target.value)}
          maxLength={200}
          hint="Optional — the name the business trades under, where it differs."
        />

        <Input
          label="Constitution"
          value={form.constitution}
          onChange={(e) => set('constitution')(e.target.value)}
          maxLength={64}
          hint={
            derived?.constitution
              ? `Read from the GSTIN's PAN. Change it if the register says finer.`
              : 'Optional — proprietorship, partnership, private limited…'
          }
        />

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-foreground">
            Registration type
          </span>
          <select
            value={form.registrationType}
            onChange={(e) => set('registrationType')(e.target.value)}
            className="gst-select h-11 w-full px-4"
          >
            {REGISTRATION_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {/* Said out loud: a GSTIN does not encode it, and its wrongness changes a
              return. The portal reports it; without the portal it is the person's. */}
          <span className="mt-1.5 block text-xs text-muted-foreground">
            Not in the GSTIN — the portal reports it; otherwise set it from the client's
            registration certificate.
          </span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Date of registration"
            type="date"
            value={form.registeredOn}
            onChange={(e) => set('registeredOn')(e.target.value)}
            hint="Optional."
          />
          <Input
            label="Pincode"
            value={form.pincode}
            onChange={(e) => set('pincode')(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            hint="Optional."
          />
        </div>
        <Input
          label="Address"
          value={form.addressLine}
          onChange={(e) => set('addressLine')(e.target.value)}
          maxLength={300}
          hint="Optional — the principal place of business."
        />
        <Input
          label="City"
          value={form.city}
          onChange={(e) => set('city')(e.target.value)}
          maxLength={100}
          hint="Optional."
        />

        {error && (
          <Alert tone={error.atCap ? 'warning' : 'error'}>
            <p>{error.message}</p>
            {error.atCap && (
              <a
                href={`${STORE_URL}/catalog/gst`}
                className="mt-2 inline-block text-sm font-medium text-primary"
              >
                See plans with more clients →
              </a>
            )}
          </Alert>
        )}

        <div className="sticky bottom-0 flex justify-end gap-3 bg-card pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!gstinLooksRight || !form.legalName.trim()}
            loading={saving}
            loadingText="Adding…"
          >
            Add client
          </Button>
        </div>
      </form>
    </Modal>
  )
}
